import React, { useCallback, useMemo, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { BackButton } from '../../ses/components/BackButton';
import SimpleBar from 'simplebar-react';
import { usePlayerShiftDetail } from '../hooks/usePlayerShiftDetail';
import { PlayerInfoHeader } from './PlayerInfoHeader';
import { PlayerStatsRow } from './PlayerStatsRow';
import { ShiftCard } from './ShiftCard';
import { PlayerShift, StatKey } from '../types/PlayerShiftTypes';
import { SportEvent } from '../../ses/types/SportEventTypes';
import { ClipEventMetadata } from '../../ses/components/ClipCreationContent';
import { useCurrentGame } from '../../games/context/CurrentGameContext';
import { useTimestampState } from '@/shared/hooks/websocket/useTimestamp';
import { getEventPayloadDescription, } from '../../ses/utils/sportEventUtils';
import { getEventPlayerAttributions } from '../utils/shiftUtils';
import { getStatDefinitions } from '../../ses/sports/registry';
import { Tracking } from '@/shared/services/tracking';

interface PlayerDetailPanelProps {
  playerId: string;
  teamSide: 'home' | 'away';
  onBack: () => void;
  onCreateClip: (event: SportEvent, metadata: ClipEventMetadata) => void;
  onDownloadShift: (shift: PlayerShift, playerId: string) => void;
  onSeekToShift: (shift: PlayerShift, playerId: string) => void;
}

export const PlayerDetailPanel: React.FC<PlayerDetailPanelProps> = ({
  playerId,
  teamSide,
  onBack,
  onCreateClip,
  onDownloadShift,
  onSeekToShift,
}) => {
  const theme = useTheme();
  const { game, homeTeamLogoPath, awayTeamLogoPath, homeTeamPrimaryColor, awayTeamPrimaryColor, findPlayerById } =
    useCurrentGame();
  const summary = usePlayerShiftDetail(playerId);
  const [, setTimestamp] = useTimestampState();
  const [activeFilters, setActiveFilters] = useState<Set<StatKey>>(new Set());

  // Stats are sport-specific: the active game's league decides which stat
  // boxes/filters to show (NHL goals/assists/…, NBA PTS/REB/…).
  const statDefs = useMemo(
    () => getStatDefinitions(game?.season?.league?.name),
    [game?.season?.league?.name],
  );

  const toggleFilter = useCallback(
    (stat: StatKey) => {
      // Derived before the updater runs: StrictMode invokes updaters twice in
      // dev, which would double-report if the event fired inside one.
      const enabled = !activeFilters.has(stat);
      Tracking.getInstance().track('Player Shift Filter', {
        category: 'Clips',
        stat,
        enabled,
        playerId,
        teamSide,
      });
      setActiveFilters((prev) => {
        const next = new Set(prev);
        if (next.has(stat)) next.delete(stat);
        else next.add(stat);
        return next;
      });
    },
    [activeFilters, playerId, teamSide],
  );

  // Find the player's headshot from the auto-iso registration
  const playerInfo = useMemo(() => findPlayerById(playerId), [findPlayerById, playerId]);

  const teamLogoPath = teamSide === 'home' ? homeTeamLogoPath : awayTeamLogoPath;
  const teamColor = teamSide === 'home' ? homeTeamPrimaryColor : awayTeamPrimaryColor;

  const handleDownloadShift = useCallback(
    (shift: PlayerShift) => {
      onDownloadShift(shift, playerId);
    },
    [onDownloadShift, playerId],
  );

  const handleSeekToShift = useCallback(
    (shift: PlayerShift) => {
      onSeekToShift(shift, playerId);
    },
    [onSeekToShift, playerId],
  );

  // The per-event icon inside a shift card. It opens the event's clip view
  // rather than creating anything, which is why the board calls it
  // `View Shift Event` — the sibling pin on the play-by-play list is
  // `View Event`. Both render the same EventActions component, so the name
  // has to be decided by the call site, not inside it.
  const handleClipCreate = useCallback(
    (event: SportEvent) => {
      Tracking.getInstance().track('View Shift Event', {
        category: 'Clips',
        playerId,
        eventId: event.id,
      });
      const description = getEventPayloadDescription(event) || event.description || '';
      const gameTime = event.clock?.time ? String(event.clock.time) : '';
      const metadata: ClipEventMetadata = {
        teamLogoPath: event._teamInfo?.logoPath || teamLogoPath,
        gameTime,
        description,
      };
      onCreateClip(event, metadata);
    },
    [onCreateClip, teamLogoPath, playerId],
  );

  const handleSeekToEvent = useCallback(
    (event: SportEvent) => {
      if (setTimestamp && event.timestamp) {
        setTimestamp(new Date(event.timestamp.getTime() - 5000));
      }
    },
    [setTimestamp],
  );

  const filteredShifts = useMemo(() => {
    if (!summary || activeFilters.size === 0) return summary?.shifts ?? [];
    return summary.shifts.filter((shift) =>
      shift.events.some((event) =>
        getEventPlayerAttributions(event).some(
          (attr) => activeFilters.has(attr.stat) && attr.playerId === playerId,
        ),
      ),
    );
  }, [summary, activeFilters, playerId]);

  const backButton = (
    <Box sx={{ px: 1.5, pb: 0.5, pt: 1.5 }}>
      <BackButton onClick={onBack} />
    </Box>
  );

  if (!summary) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {backButton}
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
            No shift data available for this player.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {backButton}
      <PlayerInfoHeader
        summary={summary}
        headshot={(playerInfo as any)?.headshot || ''}
        teamLogoPath={teamLogoPath}
        teamColor={teamColor}
      />

      <PlayerStatsRow
        stats={summary.stats}
        statDefs={statDefs}
        activeFilters={activeFilters}
        onFilterChange={toggleFilter}
      />

      {/* Player Shifts title + filter indicator */}
      <Box sx={{ px: 1.5, pt: 1, pb: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography
          variant="subtitle1"
          sx={{
            color: '#FFF',
            fontFamily: 'Sohne',
            fontSize: '20px',
            fontWeight: 500,
            lineHeight: '28px',
          }}
        >
          Player Shifts
        </Typography>
        {activeFilters.size > 0 && (
          <Typography
            sx={{ fontSize: '12px', color: theme.palette.cx.text.tertiary, lineHeight: '16px' }}
          >
            {filteredShifts.length} of {summary.shifts.length}
          </Typography>
        )}
      </Box>

      {/* Scrollable shift list */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <SimpleBar autoHide={false} style={{ height: '100%' }}>
          <Box sx={{ px: 1.5, pb: 2 }}>
            {filteredShifts.length === 0 ? (
              <Typography
                variant="body2"
                sx={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', py: 4 }}
              >
                {activeFilters.size > 0 ? 'No shifts with this stat type.' : 'No shifts recorded yet.'}
              </Typography>
            ) : (
              filteredShifts.map((shift) => (
                <ShiftCard
                  key={shift.shiftId}
                  shift={shift}
                  onDownloadShift={handleDownloadShift}
                  onClipCreate={handleClipCreate}
                  onSeekToEvent={handleSeekToEvent}
                  onSeekToShift={handleSeekToShift}
                  activeFilters={activeFilters}
                  playerId={playerId}
                />
              ))
            )}
          </Box>
        </SimpleBar>
      </Box>
    </Box>
  );
};
